import L from 'leaflet';

/**
 * Patch Leaflet prototypes to prevent unhandled exceptions and spurious console warnings
 * during teardown, animations, and headless browser runs.
 */
if (typeof window !== 'undefined' && L) {
  // 1. Guard Canvas _draw and _redraw against null/undefined canvas 2D contexts
  // which occur when an animation frame fires after or during map destruction.
  if (L.Canvas) {
    const CanvasProto = L.Canvas.prototype as unknown as {
      _draw?: (...args: unknown[]) => unknown;
      _redraw?: (...args: unknown[]) => unknown;
      _ctx?: CanvasRenderingContext2D | null;
    };

    const origDraw = CanvasProto._draw;
    if (typeof origDraw === 'function') {
      CanvasProto._draw = function (...args: unknown[]) {
        if (!this._ctx) return;
        return origDraw.apply(this, args);
      };
    }

    const origRedraw = CanvasProto._redraw;
    if (typeof origRedraw === 'function') {
      CanvasProto._redraw = function (...args: unknown[]) {
        if (!this._ctx) return;
        return origRedraw.apply(this, args);
      };
    }
  }

  // 2. Guard Evented, Map, Layer, Marker, and Path _on and _off against undefined/non-function listener functions
  // which produce spurious `wrong listener type: undefined` console warnings in Leaflet.
  const targets = [
    L.Evented?.prototype,
    L.Map?.prototype,
    L.Layer?.prototype,
    L.Marker?.prototype,
    L.Path?.prototype,
    L.Polyline?.prototype,
    L.Polygon?.prototype,
    L.FeatureGroup?.prototype,
    (L as unknown as { PosAnimation?: { prototype?: unknown } }).PosAnimation?.prototype,
  ].filter(Boolean) as Array<Record<string, unknown>>;

  for (const proto of targets) {
    const p = proto as {
      _on?: { (...args: unknown[]): unknown; __patched?: boolean };
      _off?: { (...args: unknown[]): unknown; __patched?: boolean };
    };

    if (typeof p._on === 'function' && !p._on.__patched) {
      const origOn = p._on;
      const patchedOn = function (
        this: unknown,
        type: unknown,
        fn: unknown,
        context?: unknown,
        _once?: unknown,
      ) {
        if (typeof fn !== 'function') return this;
        return origOn.call(this, type, fn, context, _once);
      };
      (patchedOn as { __patched?: boolean }).__patched = true;
      p._on = patchedOn;
    }

    if (typeof p._off === 'function' && !p._off.__patched) {
      const origOff = p._off;
      const patchedOff = function (this: unknown, ...args: unknown[]) {
        if (args.length === 0) {
          return origOff.apply(this, args);
        }
        const [type, fn, context] = args;
        if (fn === undefined) {
          return origOff.call(this, type);
        }
        if (typeof fn !== 'function') {
          return this;
        }
        return origOff.call(this, type, fn, context);
      };
      (patchedOff as { __patched?: boolean }).__patched = true;
      p._off = patchedOff;
    }
  }
}
